import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, Syringe, Beaker, Layers } from "lucide-react";
import api from "../lib/api";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

export default function DosageCalculator() {
  const [mg, setMg] = useState("10");
  const [ml, setMl] = useState("2");
  const [mcg, setMcg] = useState("250");
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const onSelectProduct = (id) => {
    setSelected(id);
    const p = products.find((x) => x.id === id);
    if (!p) return;
    const mgMatch = (p.name || "").match(/(\d+(?:\.\d+)?)\s*mg/i);
    if (mgMatch) setMg(mgMatch[1]);
    const mlMatch = (p.reconstitution || "").match(/(\d+(?:\.\d+)?)\s*ml/i);
    if (mlMatch) setMl(mlMatch[1]);
  };

  const res = useMemo(() => {
    const M = num(mg), W = num(ml), D = num(mcg);
    if (!(M > 0) || !(W > 0) || !(D > 0)) return { valid: false };
    const totalMcg = M * 1000;
    const mcgPerMl = totalMcg / W;
    const mlDraw = D / mcgPerMl;
    const units = mlDraw * 100; // U-100 insulin syringe: 1 mL = 100 units
    const doses = totalMcg / D;
    return { valid: true, units, mlDraw, doses, mcgPerMl };
  }, [mg, ml, mcg]);

  const fields = [
    { label: "Total peptide in vial", unit: "mg", value: mg, set: setMg, icon: Beaker, testid: "calc-mg" },
    { label: "Bacteriostatic water added", unit: "mL", value: ml, set: setMl, icon: Layers, testid: "calc-ml" },
    { label: "Desired dose", unit: "mcg", value: mcg, set: setMcg, icon: Syringe, testid: "calc-mcg" },
  ];

  return (
    <section id="calculator" className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
      <motion.p
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="text-[11px] tracking-[0.4em] uppercase text-[#00F0FF] mb-4 font-mono2"
      >
        // Dosage Calculator
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        className="font-display font-black text-3xl md:text-5xl uppercase tracking-tighter mb-4"
      >
        Reconstitution &amp;<br /><span className="text-[#00F0FF] neon-text">Draw Calculator</span>
      </motion.h2>
      <p className="text-xs text-slate-500 font-mono2 max-w-xl mb-14">
        Enter your vial and dose to get the exact volume to draw on a U-100 insulin syringe. Research use only — always double-check your math.
      </p>

      <div className="grid lg:grid-cols-2 gap-6 items-stretch">
        {/* Inputs */}
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-6"
          data-testid="calc-inputs"
        >
          <div>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-mono2 mb-2">
              <Calculator size={14} className="text-[#00F0FF]" /> Autofill from product
            </label>
            <div className="flex items-center border border-white/15 focus-within:border-[#00F0FF] bg-black transition-colors">
              <select
                value={selected} onChange={(e) => onSelectProduct(e.target.value)}
                className="flex-1 bg-transparent outline-none px-4 py-3 text-sm font-mono2 text-white appearance-none cursor-pointer"
                data-testid="calc-product-select"
              >
                <option value="" className="bg-[#0A0A0C]">— Select a compound —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id} className="bg-[#0A0A0C]">{p.name}</option>
                ))}
              </select>
              <span className="px-4 text-[#00F0FF] text-xs">▾</span>
            </div>
          </div>

          {fields.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.testid}>
                <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-mono2 mb-2">
                  <Icon size={14} className="text-[#00F0FF]" /> {f.label}
                </label>
                <div className="flex items-center border border-white/15 focus-within:border-[#00F0FF] bg-black transition-colors">
                  <input
                    type="number" min="0" step="any" value={f.value}
                    onChange={(e) => f.set(e.target.value)}
                    className="flex-1 bg-transparent outline-none px-4 py-3 text-lg font-display font-bold"
                    data-testid={f.testid}
                  />
                  <span className="px-4 text-xs uppercase tracking-widest text-slate-500 font-mono2 border-l border-white/10">{f.unit}</span>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Results */}
        <motion.div
          initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="relative border border-[#00F0FF]/40 bg-gradient-to-b from-[#00F0FF]/[0.06] to-transparent p-6 md:p-8 flex flex-col justify-center overflow-hidden"
          data-testid="calc-results"
        >
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#00F0FF]/10 blur-[80px]" />
          {!res.valid ? (
            <p className="text-slate-500 font-mono2 text-sm relative">Enter positive values above to see your dose.</p>
          ) : (
            <div className="relative space-y-8">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-mono2 mb-2">Draw on insulin syringe</p>
                <p className="font-display font-black text-5xl md:text-6xl leading-none neon-text" data-testid="calc-units">
                  {res.units.toFixed(1)}<span className="text-lg text-slate-400 ml-2">units</span>
                </p>
                <p className="text-xs text-slate-400 font-mono2 mt-2" data-testid="calc-ml-draw">
                  = {res.mlDraw.toFixed(3)} mL &nbsp;·&nbsp; U-100 syringe (100 units = 1 mL)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-6">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2 mb-1">Doses per vial</p>
                  <p className="font-display font-black text-3xl" data-testid="calc-doses">{Math.floor(res.doses)}</p>
                  <p className="text-[11px] text-slate-500 font-mono2">({res.doses.toFixed(1)} exact)</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-mono2 mb-1">Concentration</p>
                  <p className="font-display font-black text-3xl" data-testid="calc-concentration">{Math.round(res.mcgPerMl)}</p>
                  <p className="text-[11px] text-slate-500 font-mono2">mcg / mL</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
